import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { POST_CLOSING_STAGES } from './PostClosingProgress';
import { POST_CLOSING_QUEUE_LABELS, type PostClosingQueueStage } from './postClosingQueueStage';
import type { StageAnalytics } from './use-post-closing-analytics';

interface PostClosingAnalyticsCardsProps {
  stageAnalytics: StageAnalytics[];
  isLoading: boolean;
  error?: Error | null;
  selectedStage?: PostClosingQueueStage | null;
  onStageClick?: (stageKey: PostClosingQueueStage | null) => void;
}

const STAGE_COLORS: Record<string, string> = {
  all: 'text-foreground',
  funded: 'text-green-600',
  ready_to_ship: 'text-yellow-600',
  delivered: 'text-blue-600',
  conditions: 'text-orange-600',
  cleared: 'text-emerald-600',
  pa_issued: 'text-purple-600'
};

function formatFullCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
}

// Stages to show as cards (excludes finalized — those leave the pipeline)
const VISIBLE_STAGES = POST_CLOSING_STAGES.filter(s => s.key !== 'finalized');

export function PostClosingAnalyticsCards({ stageAnalytics, isLoading, error, selectedStage, onStageClick }: PostClosingAnalyticsCardsProps) {
  // All totals — sum across all stages. Hooks must run before the early
  // error return below, or the hook order breaks when `error` toggles.
  const allTotals = useMemo(() => {
    let totalLoans = 0;
    let totalVolume = 0;
    for (const s of stageAnalytics) {
      totalLoans += s.loanCount;
      totalVolume += s.loanVolume;
    }
    return { loanCount: totalLoans, loanVolume: totalVolume };
  }, [stageAnalytics]);

  if (error && !isLoading) {
    return (
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-7">
        <Card className="col-span-full">
          <CardContent className="flex items-center gap-2 py-4">
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Post-closing stage metrics unavailable. Table results are still accurate.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stageMap = new Map(stageAnalytics.map(s => [s.stageKey, s]));

  const cardProps = (key: PostClosingQueueStage | null, isSelected: boolean) => ({
    role: 'button' as const,
    tabIndex: 0,
    className: cn(
      'cursor-pointer transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      isSelected && 'ring-2 ring-primary ring-offset-1'
    ),
    onClick: () => onStageClick?.(key),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onStageClick?.(key);
      }
    }
  });

  return (
    <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-7">
      {/* All card */}
      <Card {...cardProps(null, selectedStage === undefined || selectedStage === null)}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 px-4 pt-3">
          <CardTitle className="text-xs font-medium text-muted-foreground">All</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {isLoading ? (
            <div className="h-5 w-24 animate-pulse bg-gray-200 rounded" />
          ) : (
            <div className="text-sm font-semibold text-foreground">
              {allTotals.loanCount.toLocaleString()} Units {formatFullCurrency(allTotals.loanVolume)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stage cards (excludes finalized) */}
      {VISIBLE_STAGES.map((stage) => {
        const data = stageMap.get(stage.key);
        const loanCount = data?.loanCount ?? 0;
        const loanVolume = data?.loanVolume ?? 0;
        const color = STAGE_COLORS[stage.key] || 'text-muted-foreground';
        const isSelected = selectedStage === stage.key;

        return (
          <Card key={stage.key} {...cardProps(stage.key as PostClosingQueueStage, isSelected)}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 px-4 pt-3">
              <CardTitle className="text-xs font-medium text-muted-foreground truncate">
                {POST_CLOSING_QUEUE_LABELS[stage.key as PostClosingQueueStage] || stage.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              {isLoading ? (
                <div className="h-5 w-24 animate-pulse bg-gray-200 rounded" />
              ) : (
                <div className={`text-sm font-semibold ${color}`}>
                  {loanCount.toLocaleString()} Units {formatFullCurrency(loanVolume)}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
